<template>
  <div class="modify-con">
    <div class="head" v-if="props.data && usingHeader">
      <button @click="cancel" :aria-label="$t('label_go_back')">
        <ChevronLeftIcon />
      </button>
      <h4 class="semibold">{{ $t('label_edit_interpretation') }}</h4>
    </div>

    <div class="body" v-if="props.data">
      <div class="opt-head">
        <p class="">
          {{ $t('label_interpretation_model') }}
        </p>
        <div class="model-info">{{ interpreterTitle }} {{ maxParticipants }} {{ t('label_person_count') }}</div>
      </div>

      <div class="container-option">
        <ChatSettingRoom :form="form" />
        <ChatSettingOption :form="form" :accordion-default-open="true" />
        <ChatSettingAdvanced
          v-model:advanced-options="advancedOptions"
          v-model:is-active-advanced-settings="isActiveAdvancedSettings"
          v-model:gauge-options="gaugeOptions"
          :accordion-default-open="false"
        />
        <!-- <ChatSettingKeyword :form="form" :acco`rdion-default-open="true" /> -->
        <ChatSettingImage
          :entrance-image-url="entranceLogoImageUrl"
          :call-image-url="callLogoImageUrl"
          @entrance-file-selected="handleEntranceImageSelect"
          @call-file-selected="handleCallImageSelect"
        />
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <ButtonProcess
      v-if="props.data && usingBackButton"
      :secondary="true"
      :disabled="false"
      :class="{ disabled: false }"
      @click="cancel"
    >
      {{ $t('label_go_back') }}
    </ButtonProcess>

    <div class="button-wrapper">
      <button
        v-if="props.data && props.hasDelete"
        class="l-btn gray-btn"
        :class="{ disabled: false }"
        @click="deleteChat"
      >
        {{ $t('delete_voice') }}
      </button>
      <ButtonProcess :loading="applyLoading" :class="{ disabled: applyLoading }" @click="onApply">
        {{ $t('button_apply') }}
      </ButtonProcess>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { cloneDeep } from 'lodash';

import { deleteCall, editChatRoom } from '@/api/apiService';
import ChatSettingAdvanced from '@/components/domains/admin/main-view-owner/ChatSettingAdvanced.vue';
import ChatSettingImage from '@/components/domains/admin/main-view-owner/ChatSettingImage.vue';
import ChatSettingOption from '@/components/domains/admin/main-view-owner/ChatSettingOption.vue';
import ChatSettingRoom from '@/components/domains/admin/main-view-owner/ChatSettingRoom.vue';
import ChevronLeftIcon from '@/components/icons/IconChevronLeft.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import { useCreateOrUpdateFairConView } from '@/composables/domains/admin/useCreateOrUpdateFairConView';
import { useAlertStore } from '@/stores/alert';
import { useToastStore } from '@/stores/toast.js';
import { TypeDomain } from '@/types/common';
import { CHAT_MODES, DEFAULT_CHAT_CREATE_FORM, SERVICE_PRICE_MAP } from '@/utils/constants/chat';
import { validateDescription, validateName } from '@/utils/validators';

const props = withDefaults(
  defineProps<{
    data: any;
    usingHeader?: boolean;
    usingBackButton?: boolean;
    domain?: TypeDomain;
    hasDelete?: boolean;
  }>(),
  {
    data: null,
    usingHeader: false,
    usingBackButton: false,
    domain: 'ADMIN',
    hasDelete: true,
  }
);

const emit = defineEmits(['created', 'updated', 'deleted', 'cancel']);
const toastStore = useToastStore();
const alertStore = useAlertStore();
const { t } = useI18n();
const { advancedOptions, isActiveAdvancedSettings, gaugeOptions } = useCreateOrUpdateFairConView();

const form = reactive(cloneDeep(DEFAULT_CHAT_CREATE_FORM));
const planId = ref<string>('');
const maxParticipants = ref<number>(0);
const code = ref<string>('');

const entranceLogoImage = ref<File | null>(null);
const entranceLogoImageUrl = ref<string | null>(null);
const callLogoImage = ref<File | null>(null);
const callLogoImageUrl = ref<string | null>(null);

const saveLoading = ref<boolean>(false);
const applyLoading = ref<boolean>(false);

const interpreterTitle = computed<string>(() => {
  const chatMode = CHAT_MODES.find((m) => m.id === form.id);
  return chatMode?.titleKey ? t(chatMode.titleKey) : '';
});

const parseBooleanOptions = (options) => {
  return Object.fromEntries(Object.entries(options).map(([key, value]) => [key, value === 'true']));
};

const convertAdvancedOptionsFromPercent = (options) => {
  if (Object.keys(options).length > 0) isActiveAdvancedSettings.value = true;

  const { TSS = 0, VAD = 0, SIL = 0 } = options;
  return {
    TSS: TSS * 100,
    VAD: VAD * 20,
    SIL: SIL * 20,
  };
};

const convertAdvancedOptionsToPercent = () => {
  if (!isActiveAdvancedSettings.value) return null;
  return {
    TSS: advancedOptions.value.TSS / 100,
    VAD: advancedOptions.value.VAD / 20,
    SIL: advancedOptions.value.SIL / 20,
  };
};

const handleEntranceImageSelect = (file) => {
  entranceLogoImage.value = file;
  entranceLogoImageUrl.value = file instanceof File ? URL.createObjectURL(file) : null;
};

const handleCallImageSelect = (file) => {
  callLogoImage.value = file;
  callLogoImageUrl.value = file instanceof File ? URL.createObjectURL(file) : null;
};

const validateFormData = () => {
  const errorMessage = validateName(form.title) || validateDescription(form.description);

  if (errorMessage) {
    toastStore.error(t(errorMessage));
    return false;
  }

  return true;
};

const onApply = async () => {
  applyLoading.value = true;

  try {
    await requestSave();
    emit('updated');
  } finally {
    applyLoading.value = false;
  }
};

const requestSave = async () => {
  if (!props.data) return null;
  if (!validateFormData()) return null;

  const { callType = '', title = '', description = '', options = {} } = form;

  const body = {
    plan: planId.value,
    callType,
    title,
    description,
    keywords: [],
    options,
    partnerGroupId: null,
    advancedOptions: convertAdvancedOptionsToPercent(),
    removeEntranceLogoImage: !entranceLogoImageUrl.value,
    removeCallLogoImage: !callLogoImageUrl.value,
  };

  const formData = new FormData();
  formData.append('body', JSON.stringify(body));
  if (entranceLogoImage.value) formData.append('entranceLogoImage', entranceLogoImage.value);
  if (callLogoImage.value) formData.append('callLogoImage', callLogoImage.value);

  const response = await editChatRoom(code.value, formData);
  if (!response.success && response.errorCode === 1021) {
    toastStore.error(t('alert_duplicate_name_exists'));
    return null;
  }

  toastStore.info(t('alert_update_success'));
  return;
};

const cancel = () => {
  alertStore.confirm(
    {
      title: t('label_go_back'),
      message: t('alert_confirm_go_back'),
    },
    {
      confirm: () => {
        emit('cancel');
      },
    }
  );
};

const deleteChat = () => {
  alertStore.confirm(
    {
      title: t('button_delete'),
      message: t('alert_confirm_delete'),
      subMessage: t('alert_delete_irreversible'),
    },
    {
      confirm: async () => {
        const response = await deleteCall(code.value);
        if (response.success) {
          toastStore.info(t('alert_deleted'));
          emit('deleted', code.value);
          code.value = '';
        }
      },
    }
  );
};

onMounted(async () => {
  if (props.data) {
    const planPrefix = SERVICE_PRICE_MAP[props.data.planId]?.prefix ?? '';
    const mode = CHAT_MODES.find((m) => m.id === planPrefix);

    Object.assign(form, props.data, {
      id: planPrefix,
      callType: mode?.callType ?? props.data.callType,
      options: parseBooleanOptions(props.data.options),
    });

    code.value = props.data.code;
    planId.value = props.data.planId;
    maxParticipants.value = SERVICE_PRICE_MAP[planId.value].limitAttendee;

    entranceLogoImageUrl.value = props.data.entranceLogoImageUrl;
    callLogoImageUrl.value = props.data.callLogoImageUrl;

    advancedOptions.value = convertAdvancedOptionsFromPercent(props.data.advancedOptions);
  }
});
</script>

<style scoped lang="scss">
.modify-con {
  height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 90px;
  .container-option {
    display: flex;
    flex-direction: column;
    width: 100%;
    :deep(.content-wrapper) {
      display: block;
      .title {
        margin: 0;
        padding-bottom: 0.5rem;
      }
    }
    .accordion-container {
      position: relative;
      padding: 0 1.25rem 2.5rem;
      &:first-child {
        margin-bottom: 2.5rem;
        &::before {
          content: '';
          position: absolute;
          bottom: 0;
          width: calc(100% + 40px);
          left: -20px;
          height: 1px;
          background: $color-gray-40;
        }
      }

      &:nth-child(3) {
        display: none;
      }
      :deep(.accordion-header) {
        padding-bottom: 1.5rem;
      }
      :deep(.accordion-body) {
        .opt-item {
          padding: 1rem 0;
          font-size: $font-size-psmall;
        }
      }
    }
  }
}

.head {
  display: flex;
  width: 100%;
  margin-bottom: 1.5rem;
  align-items: center;
  gap: 2rem;
}

.opt-head {
  text-align: center;
  width: 100%;
  p {
    font-size: $font-size-psmall;
  }
  .model-info {
    margin-top: 0.25rem;
    display: inline-block;
    background: $gray10;
    padding: 0;
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
    border-radius: 100px;
  }
}

.body {
  position: relative;
  display: flex;
  flex: 1;
  margin-bottom: 1rem;
  flex-direction: column;
  align-items: flex-start;
}

.image-uploader-wrapper {
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.modal-footer {
  position: absolute;
  display: flex;
  justify-content: flex-end;
  bottom: 0;
  width: 100%;
  left: 0;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  background-color: var(--font-white);
  border-top: 1px solid $color-gray-40;
  .button-wrapper {
    display: flex;
    width: 100%;
  }
}

.small-comment {
  position: relative;
  margin-top: 0.25rem;
  color: var(--font-dim-dark);
}

.error-comment {
  color: var(--color-red);
  margin-top: 5px;
}

.count-text {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 0.25rem;
  margin-left: 0.25rem;
  gap: 1rem;
  color: var(--font-dim);
}

.count-total {
  position: relative;
  white-space: nowrap;
  right: 0.25rem;
}

.info-wrap {
  display: flex;
  gap: 0.25rem;
}

.error-wrap {
  display: flex;
  gap: 0.25rem;
  margin-left: 0.25rem;
  color: var(--error);
}

.info-icon {
  position: relative;
  top: 0.125rem;
  width: 1rem;
  height: 1rem;
  fill: var(--font-dim);
}

.error-icon {
  position: relative;
  top: 0.125rem;
  width: 1rem;
  height: 1rem;
  fill: var(--error);
}

.keyword-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dropdown-wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 0.5rem;
}

.keyword-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background-color: var(--surface-gray-dimmer);
  border-radius: 8px;
  padding: 0.75rem;
}

.chip-wrapper {
  display: flex;
  gap: 0.25rem;
}

.dropdown-trigger {
  display: flex;
  width: 8rem;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-radius: 8px;
  border: solid 1px var(--border-dark);
  flex-shrink: 0;
  cursor: pointer;
}

.dropdown-trigger p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.dropdown-trigger:hover,
.dropdown-trigger.active {
  background-color: var(--surface-gray-dimmest);
}

.input-keyword {
  width: 100%;
}

.input-keyword-wrapper {
  display: flex;
  width: 100%;
  flex-direction: row;
}

::v-deep(.dropdown-content) {
  width: 19rem;
}

@media (max-width: 768px) {
  .dropdown-trigger {
    width: 6rem;
  }
  ::v-deep(.dropdown-content) {
    width: 6rem;
  }
}
</style>
